<template>
  <f7-page>
    <f7-navbar title="Revisión de formatos" :back-link="$t('atras')">
      <f7-nav-right>
        <f7-button fill icon-f7="arrow_clockwise" text="Actualizar" class="display-flex" @click="cargar()"></f7-button>
      </f7-nav-right>
    </f7-navbar>

    <f7-block strong class="no-margin-bottom">
      <p class="no-margin-top">
        Formatos que la Dirección Técnica tiene que revisar. Esta consulta <b>no cambia ningún dato</b>:
        qué hacer con cada formato —corregirlo, retirarlo de uso o ponerle fecha de entrada en vigor— lo
        decide la Dirección Técnica.
      </p>
      <p v-if="revision" class="no-margin-bottom texto-secundario">
        Generada el {{ fecha(revision.generado, 'DD/MM/YYYY HH:mm') }} sobre {{ revision.totalFormatos }} formatos.
      </p>
      <p v-if="error" class="no-margin-bottom text-color-red">{{ error }}</p>
    </f7-block>

    <template v-if="revision">
      <f7-block-title medium>Formatos de prueba o duplicados marcados en uso ({{ revision.pruebasEnUso.length }})</f7-block-title>
      <f7-block class="no-margin-vertical texto-secundario">
        Están marcados como inspeccionables y llevan «prueba», «duplicado» o «no usar» en el nombre o en el
        código. Es una lista de candidatos: el motivo dice qué palabra se ha encontrado.
      </f7-block>
      <div class="card data-table">
        <table>
          <thead>
            <tr>
              <th class="label-cell">Código</th>
              <th class="label-cell">Nombre</th>
              <th class="label-cell">Motivo</th>
              <th class="label-cell">En vigor desde</th>
              <th class="label-cell">Hasta</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in revision.pruebasEnUso" :key="'p' + f.id">
              <td class="label-cell">{{ f.codigo }}</td>
              <td class="label-cell">{{ f.nombre }}</td>
              <td class="label-cell">{{ f.motivo }}</td>
              <td class="label-cell">{{ fecha(f.fechaDesde) }}</td>
              <td class="label-cell">{{ fecha(f.fechaHasta) }}</td>
              <td><f7-link v-if="puedeVer(f)" icon-f7="eye" color="green" :href="`/formatos/view/${f.id}/`" v-tooltip="'Ver'"></f7-link></td>
            </tr>
            <tr v-if="!revision.pruebasEnUso.length"><td colspan="6" class="label-cell">No hay ninguno.</td></tr>
          </tbody>
        </table>
      </div>

      <f7-block-title medium>Códigos repetidos ({{ revision.codigosRepetidos.length }})</f7-block-title>
      <f7-block class="no-margin-vertical texto-secundario">
        Varios formatos con el mismo código, sin distinguir mayúsculas ni espacios. Al buscar un formato por su
        código, la aplicación no sabe cuál de ellos usar.
      </f7-block>
      <div class="card data-table">
        <table>
          <thead>
            <tr>
              <th class="label-cell">Código</th>
              <th class="label-cell">Nombre</th>
              <th class="label-cell">En uso</th>
              <th class="label-cell">En vigor desde</th>
              <th class="label-cell">Hasta</th>
              <th class="label-cell">Edición</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="grupo in revision.codigosRepetidos">
              <tr v-for="(f, i) in grupo.formatos" :key="'c' + grupo.codigo + f.id" :class="{ 'inicio-grupo': i === 0 }">
                <td class="label-cell"><b v-if="i === 0">{{ grupo.codigo }}</b><span v-else class="texto-secundario">{{ f.codigo }}</span></td>
                <td class="label-cell">{{ f.nombre }}</td>
                <td class="label-cell">{{ f.inspeccionable ? 'Sí' : 'No' }}</td>
                <td class="label-cell">{{ fecha(f.fechaDesde) }}</td>
                <td class="label-cell">{{ fecha(f.fechaHasta) }}</td>
                <td class="label-cell">{{ edicion(f) }}</td>
                <td><f7-link v-if="puedeVer(f)" icon-f7="eye" color="green" :href="`/formatos/view/${f.id}/`" v-tooltip="'Ver'"></f7-link></td>
              </tr>
            </template>
            <tr v-if="!revision.codigosRepetidos.length"><td colspan="7" class="label-cell">No hay ninguno.</td></tr>
          </tbody>
        </table>
      </div>

      <f7-block-title medium>En uso sin fecha de entrada en vigor ({{ revision.enUsoSinFechaVigor.length }})</f7-block-title>
      <f7-block class="no-margin-vertical texto-secundario">
        Están marcados como inspeccionables pero no tienen fecha de entrada en vigor. Mientras no la tengan se
        siguen modificando directamente, sin crear edición nueva: la protección empieza en esa fecha.
      </f7-block>
      <div class="card data-table">
        <table>
          <thead>
            <tr>
              <th class="label-cell">Código</th>
              <th class="label-cell">Nombre</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in revision.enUsoSinFechaVigor" :key="'s' + f.id">
              <td class="label-cell">{{ f.codigo }}</td>
              <td class="label-cell">{{ f.nombre }}</td>
              <td><f7-link v-if="puedeVer(f)" icon-f7="eye" color="green" :href="`/formatos/view/${f.id}/`" v-tooltip="'Ver'"></f7-link></td>
            </tr>
            <tr v-if="!revision.enUsoSinFechaVigor.length"><td colspan="3" class="label-cell">No hay ninguno.</td></tr>
          </tbody>
        </table>
      </div>
    </template>
  </f7-page>
</template>
<style scoped>
  .texto-secundario {
    color: #6b7a90;
    font-size: 13px;
  }
  .inicio-grupo td {
    border-top: 2px solid #d7dde5;
  }
</style>
<script>
import moment from 'moment';
import Formato from "./../../js/db/Formato.js";
import RoutesUtils from "./../../js/RoutesUtils.js";
import preloaderDeLaSesion from "./../../js/utils/preloaderDeLaSesion.js";

// #1001 · Listado de revisión de formatos para la Dirección Técnica. Solo consulta.
export default {
  data() {
    return {
      revision: null,
      error: null
    };
  },
  methods: {
    cargar() {
      const self = this;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      self.error = null;
      Formato.revisionCalidad()
        .then(function(res) {
          self.revision = res;
        })
        .catch(function(error) {
          self.error = (error && (error.message || error.error)) || 'No se ha podido generar el listado.';
        })
        .then(function() {
          espera.cerrar();
        });
    },
    fecha(valor, formato) {
      return valor ? moment(valor).format(formato || 'DD/MM/YYYY') : '—';
    },
    // Una ficha a la que no se puede entrar no lleva enlace: daría un 403.
    puedeVer(f) {
      return RoutesUtils.tieneAcceso('/formatos/view/' + f.id + '/', ['ADMIN', 'INSPECTOR']);
    },
    edicion(f) {
      if (f.versionAnterior && f.versionSiguiente) return 'Intermedia';
      if (f.versionSiguiente) return 'Tiene edición nueva';
      if (f.versionAnterior) return 'Edición nueva de otra';
      return '—';
    }
  },
  mounted() {
    this.cargar();
  }
};
</script>
